import React, { useState } from "react";
import {
	Card,
	CardActionArea,
	Typography,
	IconButton,
	Tooltip,
	Zoom,
	Button,
	Checkbox,
} from "@material-ui/core";
import {
	FileCopy,
	PictureAsPdf,
	ZoomIn,
	CheckCircle,
} from "@material-ui/icons";
import format from "date-fns/format";
import { Skeleton } from "@material-ui/lab";
import { MediaFileType, getMediaTypeName } from "../utils/constants";
import { useMediaItemStyles } from "../utils/styles";
import MediaPlayer from "./MediaPlayer";
import ImagePreviewModal from "./ImagePreviewModal";

const MediaItem = ({
	media,
	onCopy,
	resources,
	selectionMode = false,
	isSelected = false,
	onToggleSelection,
	mediaFiles,
}) => {
	const classes = useMediaItemStyles();
	const [loaded, setLoaded] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [copying, setCopying] = useState(false);
	const [showCopySuccess, setShowCopySuccess] = useState(false);

	const isImage = media.mediaFileType === MediaFileType.IMAGE;
	const isPdf = media.mediaFileType === MediaFileType.PDF;

	const isVideoAudio = [MediaFileType.VIDEO, MediaFileType.AUDIO].includes(
		media.mediaFileType
	);

	const thumbnailUrl = isImage || isPdf ? media.extraSmallContent : null;

	const handleMediaLoad = () => {
		setLoaded(true);
	};

	const handleCopy = (e) => {
		e.stopPropagation();
		setCopying(true);
		setShowCopySuccess(true);
		onCopy(media.originalContent);

		setTimeout(() => {
			setCopying(false);
		}, 1500);
	};

	const handleOpenPreview = () => {
		if (isImage) {
			setPreviewOpen(true);
		}
	};

	const handleClosePreview = () => {
		setPreviewOpen(false);
	};

	const handleCheckboxClick = (e) => {
		e.stopPropagation();
		onToggleSelection(media);
	};

	const fileTypeName = getMediaTypeName(media.mediaFileType, resources);
	const createdDate = media.createdOn
		? format(new Date(media.createdOn), "MMM d, yyyy")
		: "";

	const renderPdfContent = () => {
		return (
			<Button
				component="a"
				href={media.originalContent}
				target="_blank"
				className={classes.pdfButton}
				disableRipple
				id={`pdf-button-${media.id}`}
			>
				<div className={classes.pdfContainer}>
					{!loaded && (
						<Skeleton
							variant="rect"
							className={classes.skeletonMedia}
							animation="wave"
						/>
					)}

					{thumbnailUrl ? (
						<img
							src={thumbnailUrl}
							alt={media.description || fileTypeName}
							className={classes.pdfThumbnail}
							onLoad={handleMediaLoad}
							style={{ display: loaded ? "block" : "none" }}
						/>
					) : (
						<div className={classes.pdfPlaceholder}>
							<PictureAsPdf className={classes.pdfIcon} />
						</div>
					)}

					<div className={classes.pdfOverlay}>
						<Typography variant="caption" className={classes.pdfCaption}>
							{media.description || resources["PDFDocument"]}
						</Typography>
					</div>
				</div>
			</Button>
		);
	};

	const renderMediaContent = () => {
		if (media.isPlaceholder) {
			return (
				<div className={classes.mediaContainer}>
					<Skeleton
						variant="rect"
						className={classes.skeletonMedia}
						animation="wave"
					/>
				</div>
			);
		}

		if (isVideoAudio) {
			return (
				<div className={classes.mediaContainer}>
					{!loaded && (
						<Skeleton
							variant="rect"
							className={classes.skeletonMedia}
							animation="wave"
						/>
					)}
					<div className={classes.videoMediaContainer}>
						<MediaPlayer
							file={media}
							limitedControls
							onLoadMedia={handleMediaLoad}
							style={{ display: loaded ? "block" : "none" }}
						/>
					</div>
				</div>
			);
		}

		if (isPdf) {
			return renderPdfContent();
		}

		return (
			<CardActionArea onClick={handleOpenPreview} id={`media-${media.id}`}>
				<div className={classes.mediaContainer}>
					{!loaded && (
						<Skeleton
							variant="rect"
							className={classes.skeletonMedia}
							animation="wave"
						/>
					)}
					{isImage && (
						<img
							src={thumbnailUrl}
							alt={media.description || fileTypeName}
							className={classes.media}
							onLoad={handleMediaLoad}
							style={{ display: loaded ? "block" : "none" }}
						/>
					)}

					<div className={classes.overlay}>
						<div className={classes.overlayTop}>
							<ZoomIn className={classes.zoomIcon} />
						</div>
						<div className={classes.overlayBottom}>
							<Typography variant="caption">{createdDate}</Typography>
						</div>
					</div>
				</div>
			</CardActionArea>
		);
	};

	return (
		<>
			<Card
				className={`${
					isVideoAudio || isPdf ? classes.staticRoot : classes.root
				} ${selectionMode ? classes.selectionModeCard : ""}`}
				elevation={1}
				onClick={
					!selectionMode && isImage && !media.isPlaceholder
						? handleOpenPreview
						: undefined
				}
				style={isSelected ? { border: "2px solid #53B3EA" } : {}}
			>
				<div className={classes.mediaWrapper}>
					{selectionMode && !media.isPlaceholder && (
						<div className={classes.checkboxContainer}>
							<Checkbox
								id={`media-${media.id}-checkbox`}
								checked={isSelected}
								onChange={handleCheckboxClick}
								className={classes.selectionCheckbox}
							/>
						</div>
					)}

					{!selectionMode &&
						!media.isPlaceholder &&
						(!isVideoAudio || !isPdf) && (
							<Tooltip title={resources["CopyURLTooltip"]}>
								<IconButton
									size="small"
									onClick={handleCopy}
									className={classes.copyButton}
									id={`copy-url-${media.id}`}
								>
									<FileCopy className={classes.copyIcon} />
								</IconButton>
							</Tooltip>
						)}

					{renderMediaContent()}

					{showCopySuccess && (
						<div className={classes.copySuccessWrapper}>
							<Zoom
								in={copying}
								timeout={{ enter: 300, exit: 500 }}
								onExited={() => setShowCopySuccess(false)}
							>
								<div className={classes.copySuccessBadge}>
									<CheckCircle className={classes.successIcon} />
								</div>
							</Zoom>
						</div>
					)}
				</div>
			</Card>

			{isImage && !media.isPlaceholder && (
				<ImagePreviewModal
					open={previewOpen}
					file={media}
					imageFiles={mediaFiles}
					onClose={handleClosePreview}
					resources={resources}
				/>
			)}
		</>
	);
};

const arePropsEqual = (prevProps, nextProps) => {
	return (
		prevProps.media.id === nextProps.media.id &&
		prevProps.media.mediaFileType === nextProps.media.mediaFileType &&
		prevProps.selectionMode === nextProps.selectionMode &&
		prevProps.isSelected === nextProps.isSelected
	);
};

export default React.memo(MediaItem, arePropsEqual);
